import type { Booking } from '@/types';

/**
 * A booking an auto-adjust type split at a non-working day is saved as one booking per run
 * of working days, all carrying the same `autoAdjustGroupId`. The timeline treats those
 * pieces as one booking: selecting, moving or deleting one takes the rest with it, and while
 * the group holds more than one piece resizing is refused, since a piece's dates are decided
 * by the working days it covers. A group re-planned back onto one piece resizes as usual.
 */

/**
 * The resources a saved booking holds. A booking that auto-adjusts is planned per resource,
 * so the row a save answers with keeps only the resources that work its span — a resource
 * that cannot work a day inside it is booked on pieces of its own instead. Its own list
 * therefore wins over the requested one, which would otherwise book every resource over
 * this piece's days as well as over its own pieces.
 */
export function savedResourceIds(
  saved: Pick<Booking, 'resourceIds'>,
  requested: readonly (string | number)[] | undefined,
): string[] | undefined {
  if (saved.resourceIds && saved.resourceIds.length > 0) return saved.resourceIds.map(String);
  return requested && requested.length > 0 ? requested.map(String) : undefined;
}

/**
 * The series the pieces belong to. A piece saved before a booking's series membership was
 * carried onto its pieces holds none of its own, so the group answers as a whole: the
 * booking the user sees is an occurrence of a series if any of its pieces is.
 */
export function autoAdjustGroupRecurrenceGroupId(
  pieces: readonly Booking[],
): string | undefined {
  return pieces.find(piece => piece.recurrenceGroupId)?.recurrenceGroupId;
}

/**
 * The clicked piece as the booking the user sees: an occurrence of the series its group
 * belongs to, so editing or deleting it offers the choice between this booking and all
 * future ones whichever piece was clicked.
 */
export function withAutoAdjustGroupRecurrence(
  booking: Booking,
  pieces: readonly Booking[],
): Booking {
  if (booking.recurrenceGroupId) return booking;

  const recurrenceGroupId = autoAdjustGroupRecurrenceGroupId(pieces);
  return recurrenceGroupId ? { ...booking, recurrenceGroupId } : booking;
}

/** Whether the booking is one piece of a booking that was split. */
export const isAutoAdjustGroupPiece = (booking: Booking): boolean =>
  Boolean(booking.autoAdjustGroupId);

/**
 * Whether the booking reads as split: its group holds more than one piece. A group
 * re-planned onto days it can work without a break is one piece again, and behaves like an
 * ordinary booking even though it keeps its group id.
 */
export const isSplitAutoAdjustBooking = (
  booking: Booking,
  all: readonly Booking[],
): boolean => autoAdjustGroupPieceCount(autoAdjustGroupPieces(booking, all)) > 1;

/**
 * How many pieces the group holds: the count the API read where it has one, so a booking
 * whose other pieces lie outside the range the timeline loaded still reads as split, and
 * otherwise the number of pieces held.
 */
export function autoAdjustGroupPieceCount(pieces: readonly Booking[]): number {
  return pieces.reduce(
    (count, piece) => Math.max(count, piece.autoAdjustGroupPieceCount ?? 0),
    pieces.length);
}

/** The ids of the groups holding more than one piece. */
export function splitAutoAdjustGroupIds(bookings: readonly Booking[]): Set<string> {
  const counts = new Map<string, number>();

  for (const booking of bookings) {
    const groupId = booking.autoAdjustGroupId;
    if (groupId) counts.set(groupId, (counts.get(groupId) ?? 0) + 1);
  }

  return new Set([...counts].filter(([, count]) => count > 1).map(([groupId]) => groupId));
}

/** The pieces of the booking's group, `booking` first, or just the booking if it has none. */
export function autoAdjustGroupPieces(
  booking: Booking,
  all: readonly Booking[],
): Booking[] {
  if (!booking.autoAdjustGroupId) return [booking];

  const pieces = all.filter(candidate =>
    candidate.autoAdjustGroupId === booking.autoAdjustGroupId
    && String(candidate.id) !== String(booking.id));

  return [booking, ...pieces];
}

/** The bookings plus every piece belonging to the same groups, de-duplicated. */
export function withAutoAdjustGroupPieces(
  bookings: readonly Booking[],
  all: readonly Booking[],
): Booking[] {
  const resolved: Booking[] = [];
  const seen = new Set<string>();

  for (const booking of bookings) {
    for (const piece of autoAdjustGroupPieces(booking, all)) {
      const id = String(piece.id);
      if (seen.has(id)) continue;
      seen.add(id);
      resolved.push(piece);
    }
  }

  return resolved;
}

/**
 * Every resource the pieces are assigned to between them. A day only some of a booking's
 * resources work is booked for those resources alone, on a piece of its own, so a single
 * piece holds only part of the booking's resources — the group holds them all.
 */
export function autoAdjustGroupResourceIds(pieces: readonly Booking[]): string[] {
  const ids: string[] = [];

  for (const piece of pieces) {
    const held = piece.resourceIds?.length
      ? piece.resourceIds
      : (piece.resourceId != null ? [piece.resourceId] : []);

    for (const id of held) {
      const key = String(id);
      if (key !== '' && !ids.includes(key)) ids.push(key);
    }
  }

  return ids;
}

/**
 * The resources the booking reads as being assigned to. A booking is planned per resource,
 * so a piece is saved for the resources sharing the days it covers while the one booking
 * the user sees is assigned to every resource its group holds: a piece answers with its
 * group's resources so a multi-resource booking reads as one wherever it is shown.
 */
export function bookingResourceIds(
  booking: Booking,
  all: readonly Booking[],
): string[] {
  const held = autoAdjustGroupResourceIds([booking]);
  if (!booking.autoAdjustGroupId) return held;

  const group = autoAdjustGroupResourceIds(autoAdjustGroupPieces(booking, all));
  return group.length > held.length ? group : held;
}

/** The span a split booking covers, and the piece a move of that whole span is saved on. */
export interface AutoAdjustGroupSpan {
  anchor: Booking;
  startTime: string;
  endTime: string;
}

/**
 * The span the pieces cover together, held on the piece that starts first. Moving a split
 * booking moves that span: the server re-plans it over the working days at its new place,
 * which are not necessarily the days each piece covers today.
 */
export function autoAdjustGroupSpan(
  pieces: readonly Booking[],
): AutoAdjustGroupSpan | null {
  let anchor: Booking | undefined;
  let endTime: string | undefined;

  for (const piece of pieces) {
    const start = new Date(piece.startTime).getTime();
    const end = new Date(piece.endTime).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) continue;

    if (!anchor || start < new Date(anchor.startTime).getTime()) anchor = piece;
    if (!endTime || end > new Date(endTime).getTime()) endTime = piece.endTime;
  }

  if (!anchor || !endTime) return null;

  // The API answers with the span the whole group covers, so a booking reaching beyond the
  // range the timeline loaded is held over the whole of it rather than over the part on
  // screen — the days it made up at the end can fall months later.
  const read = pieces.find(piece =>
    piece.autoAdjustGroupStartTime && piece.autoAdjustGroupEndTime);
  if (read?.autoAdjustGroupStartTime && read.autoAdjustGroupEndTime) {
    const startTime = earlierOf(read.autoAdjustGroupStartTime, anchor.startTime);
    if (startTime !== anchor.startTime) anchor = { ...anchor, startTime };
    endTime = laterOf(read.autoAdjustGroupEndTime, endTime);
  }

  // The save the span is held for stands for the whole booking, so the anchor carries every
  // resource the group is assigned to and not only the ones its own piece holds.
  const resourceIds = autoAdjustGroupResourceIds(pieces);
  const held = autoAdjustGroupResourceIds([anchor]);
  const holdsAll = held.length === resourceIds.length;

  return {
    anchor: holdsAll ? anchor : { ...anchor, resourceIds },
    startTime: anchor.startTime,
    endTime,
  };
}

/**
 * The span a booking already opened on should hold once the pieces known of it change: the
 * span the pieces cover where that reaches further, and nothing where it does not. A piece
 * whose days were made up in a later month is outside the range the timeline loaded, so the
 * pieces it holds cover only part of the booking — a span read from them would otherwise
 * narrow the booking to the part of it on screen.
 */
export function widenedAutoAdjustGroupSpan(
  editing: Pick<Booking, 'startTime' | 'endTime'>,
  pieces: readonly Booking[],
): { startTime: string; endTime: string } | null {
  const span = autoAdjustGroupSpan(pieces);
  if (!span) return null;

  const startTime = earlierOf(span.startTime, editing.startTime);
  const endTime = laterOf(span.endTime, editing.endTime);

  return startTime === editing.startTime && endTime === editing.endTime
    ? null
    : { startTime, endTime };
}

function earlierOf(a: string, b: string): string {
  const left = new Date(a).getTime();
  const right = new Date(b).getTime();
  if (Number.isNaN(left)) return b;
  if (Number.isNaN(right)) return a;
  return left < right ? a : b;
}

function laterOf(a: string, b: string): string {
  const left = new Date(a).getTime();
  const right = new Date(b).getTime();
  if (Number.isNaN(left)) return b;
  if (Number.isNaN(right)) return a;
  return left > right ? a : b;
}

/**
 * The booking an edit of a split booking is opened on: the piece that starts first, over
 * the span the pieces cover together. The form then holds the one booking the user sees
 * rather than the piece that happened to be clicked.
 */
export function autoAdjustGroupEditTarget(
  booking: Booking,
  all: readonly Booking[],
): Booking {
  const pieces = autoAdjustGroupPieces(booking, all);
  if (autoAdjustGroupPieceCount(pieces) < 2) return booking;

  const span = autoAdjustGroupSpan(pieces);
  return span ? { ...span.anchor, startTime: span.startTime, endTime: span.endTime } : booking;
}

/** A save an edit of a split booking turns into. */
export interface AutoAdjustGroupSave {
  id: string | number;
  update: Partial<Booking>;
}

/**
 * The saves an edit of a split booking turns into: one save of the whole span on the piece
 * that starts first when the edit moved or resized the booking, so the server re-plans the
 * group over the working days it now covers, or the edited details on every piece when it
 * did not, so the group carries them all rather than only the piece that was opened.
 *
 * Whether the booking was moved or resized is read against `editedFrom`, the booking the
 * form was opened on, rather than against the pieces: a save carrying back the span the
 * form was given is no move, and re-planning it would have the server plan the booking as
 * if the span it was asked for were the span the days it made up at the end left it
 * covering — losing those days.
 */
export function planAutoAdjustGroupEdit(
  pieces: readonly Booking[],
  update: Partial<Booking>,
  editedFrom?: Booking,
): AutoAdjustGroupSave[] {
  const span = autoAdjustGroupSpan(pieces);
  const anchor = span?.anchor ?? pieces[0];
  if (!anchor) return [];

  const editedSpan = editedFrom
    ? { startTime: editedFrom.startTime, endTime: editedFrom.endTime }
    : span;

  if (!span
    || !editedSpan
    || spanChanged(editedSpan, update)
    || resourcesChanged(editedFrom ?? anchor, update)) {
    return [{ id: anchor.id, update }];
  }

  const details: Partial<Booking> = { ...update };
  delete details.startTime;
  delete details.endTime;
  // This save leaves the booking's resources as they are, and a piece covering a day only
  // some of them work is assigned those alone, so each piece keeps its own.
  delete details.resourceId;
  delete details.resourceIds;

  return pieces.map(piece => ({ id: piece.id, update: details }));
}

const isSameDay = (a: string, b: string): boolean =>
  new Date(a).toDateString() === new Date(b).toDateString();

/** Whether the edit moved or resized the booking the pieces cover together. */
function spanChanged(
  span: { startTime: string; endTime: string },
  update: Partial<Booking>,
): boolean {
  return (update.startTime != null && !isSameDay(update.startTime, span.startTime))
    || (update.endTime != null && !isSameDay(update.endTime, span.endTime));
}

/**
 * Whether the edit put the booking on other resources. Their working days are their own,
 * so the group is re-planned even when it keeps its dates.
 */
function resourcesChanged(anchor: Booking, update: Partial<Booking>): boolean {
  if (update.resourceIds != null) {
    const held = (anchor.resourceIds ?? (anchor.resourceId != null ? [anchor.resourceId] : []))
      .map(String);
    const next = update.resourceIds.map(String);
    return next.length !== held.length || next.some(id => !held.includes(id));
  }

  return update.resourceId != null && String(update.resourceId) !== String(anchor.resourceId);
}

/** A booking's new window in a move. */
export interface AutoAdjustGroupMove {
  booking: Booking;
  newStartTime: Date;
  newEndTime: Date;
}

/**
 * The move a drag of one piece of a split booking stands for: the whole span the pieces
 * cover, shifted by the distance the piece was dragged and held on the piece that starts
 * first. The booking the user drags is the group, so the server is asked to re-plan the
 * whole of it over the working days at its new place rather than to move the one piece,
 * which would leave the booking with only the days that piece covered. `null` when the
 * booking is not split, which moves on its own as any other booking does.
 */
export function autoAdjustGroupDrag(
  booking: Booking,
  all: readonly Booking[],
  newStartTime: Date,
): AutoAdjustGroupMove | null {
  const pieces = autoAdjustGroupPieces(booking, all);
  if (autoAdjustGroupPieceCount(pieces) < 2) return null;

  const span = autoAdjustGroupSpan(pieces);
  if (!span) return null;

  const spanStart = new Date(span.startTime).getTime();
  const spanEnd = new Date(span.endTime).getTime();
  const dragged = new Date(booking.startTime).getTime();
  if (Number.isNaN(spanStart) || Number.isNaN(spanEnd) || Number.isNaN(dragged)) return null;

  const moved = newStartTime.getTime() - dragged;

  return {
    booking: { ...span.anchor, startTime: span.startTime, endTime: span.endTime },
    newStartTime: new Date(spanStart + moved),
    newEndTime: new Date(spanEnd + moved),
  };
}

/**
 * The moves a drag of several bookings turns into: a split booking moves as a whole
 * wherever one of its pieces was dragged, and counts once however many of its pieces the
 * drag held.
 */
export function planAutoAdjustGroupDrags<T extends AutoAdjustGroupMove>(
  targets: readonly T[],
  all: readonly Booking[],
): T[] {
  const planned: T[] = [];
  const groups = new Set<string>();

  for (const target of targets) {
    const groupId = target.booking.autoAdjustGroupId;
    const move = autoAdjustGroupDrag(target.booking, all, target.newStartTime);

    if (!move || !groupId) {
      planned.push(target);
      continue;
    }

    if (groups.has(groupId)) continue;
    groups.add(groupId);
    planned.push({ ...target, ...move });
  }

  return planned;
}

/**
 * Collapses the pieces of each split booking in a move into one move covering the whole
 * span they were dropped on, kept on the piece that lands first. The move is then the one
 * booking the user dragged, which lets the server re-plan it over the working days at its
 * new place — the days it skips there may well not be the ones it skipped before.
 */
export function collapseAutoAdjustGroupMoves<T extends AutoAdjustGroupMove>(
  targets: readonly T[],
): T[] {
  const collapsed: T[] = [];
  const byGroup = new Map<string, T>();

  for (const target of targets) {
    const groupId = target.booking.autoAdjustGroupId;
    if (!groupId) {
      collapsed.push(target);
      continue;
    }

    const held = byGroup.get(groupId);
    if (!held) {
      byGroup.set(groupId, target);
      collapsed.push(target);
      continue;
    }

    const first = target.newStartTime < held.newStartTime ? target : held;
    // The one move stands for the whole booking, so it keeps every resource the pieces are
    // assigned to: a piece covering a day only some of them work holds those alone.
    const resourceIds = autoAdjustGroupResourceIds([held.booking, target.booking]);
    const booking = resourceIds.length > autoAdjustGroupResourceIds([first.booking]).length
      ? { ...first.booking, resourceIds }
      : first.booking;
    const merged = {
      ...first,
      booking,
      newStartTime: first.newStartTime,
      newEndTime: target.newEndTime > held.newEndTime ? target.newEndTime : held.newEndTime,
    };

    byGroup.set(groupId, merged);
    collapsed[collapsed.indexOf(held)] = merged;
  }

  return collapsed;
}

/** The group ids among the bookings, de-duplicated and in the order they appear. */
export function autoAdjustGroupIds(
  bookings: readonly (Booking | undefined)[],
): string[] {
  const ids: string[] = [];

  for (const booking of bookings) {
    const groupId = booking?.autoAdjustGroupId;
    if (groupId && !ids.includes(groupId)) ids.push(groupId);
  }

  return ids;
}

/**
 * The bookings to read a split booking's span, pieces and resources from, once its groups
 * have been read from the API. The rows read stand for their whole group: the timeline
 * holds only the pieces inside the range it loaded, so a booking whose made-up days were
 * pushed into a later month is on screen as the part of it that range covers, and reading
 * its span from the pieces held locally would shorten it to that part. Bookings of groups
 * that were not read, and bookings that were never split, are kept as they are held.
 */
export function withReadAutoAdjustGroupRows(
  held: readonly Booking[],
  rows: readonly Booking[],
  readGroupIds: readonly string[],
): Booking[] {
  if (rows.length === 0) return [...held];

  const read = new Set(readGroupIds);
  return [
    ...withAutoAdjustGroupSpanOfRows(rows),
    ...held.filter(booking =>
      !booking.autoAdjustGroupId || !read.has(booking.autoAdjustGroupId)),
  ];
}

/**
 * Puts the group's saved pieces in place of the ones held locally, keeping the position of
 * the first piece. A move re-plans the whole group server-side, so the pieces it comes back
 * as are the only truth: pieces the re-plan dropped go, pieces it added arrive, and local
 * details the API does not carry (resource assignments, audit history) survive on the
 * pieces that stayed.
 */
export function applyAutoAdjustGroupRows(
  bookings: readonly Booking[],
  groupId: string,
  rows: readonly Booking[],
): Booking[] {
  const heldById = new Map(bookings.map(booking => [String(booking.id), booking]));
  const merged = withAutoAdjustGroupSpanOfRows(rows.map(row => {
    const held = heldById.get(String(row.id));
    return held ? { ...held, ...row } : row;
  }));

  const applied: Booking[] = [];
  let placed = false;

  for (const booking of bookings) {
    if (booking.autoAdjustGroupId !== groupId) {
      applied.push(booking);
      continue;
    }

    if (placed) continue;
    applied.push(...merged);
    placed = true;
  }

  return placed ? applied : [...applied, ...merged];
}

/**
 * The rows a group was read as, each carrying the span and piece count of that whole read.
 * The read stands for the group, so it also replaces a span read earlier: a group re-planned
 * onto fewer days would otherwise keep reading as covering the days it used to.
 */
function withAutoAdjustGroupSpanOfRows(rows: readonly Booking[]): Booking[] {
  const groups = new Map<string, Booking[]>();

  for (const row of rows) {
    const groupId = row.autoAdjustGroupId;
    if (!groupId) continue;
    const group = groups.get(groupId);
    if (group) group.push(row);
    else groups.set(groupId, [row]);
  }

  return rows.map(row => {
    const group = row.autoAdjustGroupId ? groups.get(row.autoAdjustGroupId) : undefined;
    if (!group) return row;

    let startTime = group[0].startTime;
    let endTime = group[0].endTime;
    for (const piece of group) {
      startTime = earlierOf(startTime, piece.startTime);
      endTime = laterOf(endTime, piece.endTime);
    }

    return {
      ...row,
      autoAdjustGroupStartTime: startTime,
      autoAdjustGroupEndTime: endTime,
      autoAdjustGroupPieceCount: group.length,
    };
  });
}

/**
 * How many bookings the user sees in a set of pieces: one group counts once, so a menu
 * acting on a split booking is not treated as a bulk action on several bookings.
 */
export function distinctBookingCount(bookings: readonly Booking[]): number {
  const units = new Set<string>();

  for (const booking of bookings) {
    units.add(booking.autoAdjustGroupId
      ? `group:${booking.autoAdjustGroupId}`
      : `booking:${booking.id}`);
  }

  return units.size;
}
